import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown, Minus, ShieldCheck, ArrowRight } from "lucide-react";
import api from "../lib/api";

const Trend = ({ now, prev }) => {
  if (!now && !prev) return <span className="flex items-center gap-1 text-zinc-600"><Minus size={12} /> no activity yet</span>;
  const d = now - prev;
  if (d === 0) return <span className="flex items-center gap-1 text-zinc-500"><Minus size={12} /> same as last week</span>;
  const up = d > 0;
  return <span className={`flex items-center gap-1 ${up ? "text-[#14A89A]" : "text-amber-400"}`}>{up ? <TrendingUp size={12} /> : <TrendingDown size={12} />} {up ? "+" : ""}{d} vs last week</span>;
};

const Stat = ({ label, value, sub, accent, testid }) => (
  <div className="border border-white/10 bg-[#0A0A0A] p-4 min-w-0" data-testid={testid}>
    <div className="font-display text-3xl truncate" style={{ color: accent || "#FFFFFF" }} data-testid={`${testid}-value`}>{value}</div>
    <div className="font-body text-[10px] tracking-[0.15em] uppercase text-zinc-500 mt-1">{label}</div>
    <div className="font-body text-[11px] mt-2">{sub}</div>
  </div>
);

const TEAL = "#14A89A";
const PURPLE = "#A855F7";
const pct = (a, b) => (b > 0 ? `${Math.round((a / b) * 100)}%` : "-");

const Funnel = ({ f, accent }) => {
  const steps = [
    { label: "Visited your Beacon", value: f.visits, note: f.teaser_visits ? `incl. ${f.teaser_visits} from your public teaser` : null },
    { label: "Played your trailer", value: f.trailer, conv: pct(f.trailer, f.visits) },
    { label: "Subscribed", value: f.subscribed, conv: pct(f.subscribed, f.visits) },
  ];
  const max = Math.max(1, ...steps.map((s) => s.value));
  return (
    <div className="space-y-3" data-testid="beacon-funnel">
      {steps.map((s, i) => (
        <div key={s.label} data-testid={`beacon-funnel-step-${i}`}>
          <div className="flex items-baseline justify-between gap-2 font-body text-xs mb-1">
            <span className="text-zinc-300">{s.label}</span>
            <span className="text-white"><span data-testid={`beacon-funnel-value-${i}`}>{s.value}</span>{s.conv && <span className="text-zinc-500"> · {s.conv} of visitors</span>}</span>
          </div>
          <div className="h-2.5 bg-white/5 overflow-hidden">
            <div className="h-full transition-all duration-700" style={{ width: `${Math.max(s.value ? 3 : 0, (s.value / max) * 100)}%`, background: [TEAL, "#5F7FD8", PURPLE][i] }} />
          </div>
          {s.note && <div className="font-body text-[11px] text-zinc-600 mt-1">{s.note}</div>}
        </div>
      ))}
    </div>
  );
};

const Spark = ({ daily, accent }) => {
  const max = Math.max(1, ...daily.map((d) => d.visits));
  return (
    <div className="flex items-end gap-[3px] h-14" aria-label="Visits per day, last 30 days" data-testid="beacon-visits-spark">
      {daily.map((d) => <div key={d.day} title={`${d.day}: ${d.visits} visits`} className="flex-1 min-w-[2px]" style={{ height: `${Math.max(4, (d.visits / max) * 100)}%`, background: d.visits ? (d.visits === max ? PURPLE : accent) : "rgba(255,255,255,0.06)" }} />)}
    </div>
  );
};

// Creator dashboard: 4 headline numbers + visitor funnel. Counts only: never who.
const HealthCell = ({ label, value, hint, tone, testid, alert }) => (
  <div className="flex-1 min-w-[120px] px-3 py-2.5 border-l-2" style={{ borderColor: tone }} data-testid={testid}>
    <div className="font-display text-2xl" style={{ color: alert && value > 0 ? tone : "#FFFFFF" }}>{value}</div>
    <div className="font-body text-xs text-zinc-300">{label}</div>
    <div className="font-body text-[11px] text-zinc-500">{hint}</div>
  </div>
);

const Health = ({ h, accent }) => {
  if (!h) return null;
  return (
    <div className="mb-6" data-testid="beacon-health">
      <div className="font-body text-xs tracking-[0.15em] uppercase text-zinc-500 mb-3">Subscriber health</div>
      <div className="flex flex-wrap gap-y-3 bg-white/[0.02] py-1">
        <HealthCell testid="beacon-health-engaged" label="Active" value={h.engaged} hint="visited in the last 14 days" tone={accent} />
        <HealthCell testid="beacon-health-quiet" label="Quiet" value={h.quiet} hint="no visit in 14+ days" tone={PURPLE} />
        <HealthCell testid="beacon-health-canceling" label="Cancelling" value={h.canceling} hint="paid up until period end" tone="#f59e0b" />
        <HealthCell testid="beacon-health-failing" label="Card failing" value={h.card_failing} hint="Stripe is retrying" tone="#FF2D2D" alert />
      </div>
    </div>
  );
};
export const BeaconDashboard = ({ beacon }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get("/beacon/mine/insights").then((r) => setD(r.data)).catch(() => setErr(true)); }, []);
  const accent = TEAL;
  const paid = (beacon.price_monthly || 0) > 0;
  return (
    <div className="border border-white/10 border-t-2 bg-[#121212] p-5 mb-6" style={{ borderTopColor: PURPLE }} data-testid="beacon-dashboard">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <h2 className="font-body text-lg text-white">Your Beacon dashboard</h2>
        {paid && <Link to="/beacon/earnings" className="flex items-center gap-1 font-body text-xs hover:underline" style={{ color: PURPLE }} data-testid="beacon-dashboard-earnings-link">Earnings & payouts <ArrowRight size={12} /></Link>}
      </div>
      {err ? <p className="font-body text-sm text-zinc-500" data-testid="beacon-dashboard-error">Couldn't load your stats right now.</p> : !d ? <p className="font-body text-sm text-zinc-600">Loading…</p> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <Stat testid="beacon-stat-subscribers" label={paid ? "Subscribers" : "Members joined"} value={d.subscribers.value} sub={<Trend now={d.subscribers.new_7d} prev={d.subscribers.new_prev_7d} />} />
            <Stat testid="beacon-stat-income" label="Est. monthly" accent={PURPLE} value={paid ? `$${d.income.value.toFixed(0)}` : "Free"} sub={<span className="text-zinc-500">{paid ? `$${d.income.price}/mo × members` : "Set a price anytime"}</span>} />
            <Stat testid="beacon-stat-visits" label="Visits · 7 days" value={d.visits.value} sub={<Trend now={d.visits.value} prev={d.visits.prev} />} />
            <Stat testid="beacon-stat-lessons" label="Lesson plays · 7 days" value={d.lessons.value} sub={<Trend now={d.lessons.value} prev={d.lessons.prev} />} />
          </div>
          <Health h={d.health} accent={accent} />
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="font-body text-xs tracking-[0.15em] uppercase text-zinc-500 mb-3">Visitor journey · last 30 days</div>
              <Funnel f={d.funnel} accent={accent} />
            </div>
            <div>
              <div className="font-body text-xs tracking-[0.15em] uppercase text-zinc-500 mb-3">Visits per day · last 30 days</div>
              <Spark daily={d.daily} accent={accent} />
            </div>
          </div>
          <p className="font-body text-[11px] text-zinc-500 mt-5 flex items-start gap-1.5" data-testid="beacon-dashboard-privacy"><ShieldCheck size={13} className="text-[#14A89A] shrink-0 mt-px" />Counts only: you never see who. SovSig never records who played what. To count quiet members we keep just the date of each subscriber's last visit (no times, no pages), and your own visits aren't counted.</p>
        </>
      )}
    </div>
  );
};
